import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Chip,
  Checkbox,
  FormControlLabel,
  Paper,
  Tooltip,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import { useNotification } from "../../context/NotificationContext";
import {
  getAllPlans,
  createPlan,
  updatePlan,
  deletePlan,
  getAllFeatures,
} from "../../client/api";
import { THEME_COLORS } from "../../context/constants";
import { useAdminStyles } from "./styles";

interface Plan {
  _id: string;
  name: string;
  videoLimit: number;
  seats: number;
  pricePerUGC: number;
  isCustom: boolean;
  features?: Array<{
    _id: string;
    key: string;
    name: string;
    description?: string;
  }>;
  createdAt?: string;
  updatedAt?: string;
}

interface Feature {
  _id: string;
  key: string;
  name: string;
  description?: string;
  apiCreditEstimate: number;
  apiCreditRule: string;
}

export const PlansManagement: React.FC = () => {
  const classes = useAdminStyles();
  const { addNotification } = useNotification();

  const [plans, setPlans] = useState<Plan[]>([]);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState<Plan | null>(null);

  const [formData, setFormData] = useState({
    name: "",
    videoLimit: 0,
    seats: 0,
    pricePerUGC: 0,
    features: [] as string[],
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);

      const [plansData, featuresData] = await Promise.all([
        getAllPlans(),
        getAllFeatures(),
      ]);

      const nonCustomPlans = plansData
        .filter((plan: Plan) => !plan.isCustom)
        .map((plan: Plan) => ({
          ...plan,
          features: plan.features ?? [],
        }));

      setPlans(nonCustomPlans);
      setFeatures(featuresData ?? []);
    } catch (error) {
      console.error("Error fetching data:", error);
      addNotification("Failed to load plans and features", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDialog = (plan?: Plan) => {
    if (plan) {
      setEditingPlan(plan);
      setFormData({
        name: plan.name,
        videoLimit: plan.videoLimit,
        seats: plan.seats,
        pricePerUGC: plan.pricePerUGC,
        features: plan.features?.map((f) => f._id) ?? [],
      });
    } else {
      setEditingPlan(null);
      setFormData({
        name: "",
        videoLimit: 0,
        seats: 0,
        pricePerUGC: 0,
        features: [],
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingPlan(null);
  };

  const handleSubmit = async () => {
    try {
      if (editingPlan) {
        await updatePlan(editingPlan._id, formData);
        addNotification("Plan updated successfully", "success");
      } else {
        await createPlan(formData);
        addNotification("Plan created successfully", "success");
      }
      handleCloseDialog();
      fetchData();
    } catch (error: any) {
      console.error("Error saving plan:", error);
      addNotification(
        error?.response?.data?.message || "Failed to save plan",
        "error"
      );
    }
  };

  const handleDelete = async (planId: string) => {
    if (!window.confirm("Are you sure you want to delete this plan?")) {
      return;
    }

    try {
      await deletePlan(planId);
      addNotification("Plan deleted successfully", "success");
      fetchData();
    } catch (error: any) {
      console.error("Error deleting plan:", error);
      addNotification(
        error?.response?.data?.message || "Failed to delete plan",
        "error"
      );
    }
  };

  const handleFeatureToggle = (featureId: string) => {
    setFormData((prev) => ({
      ...prev,
      features: prev.features.includes(featureId)
        ? prev.features.filter((id) => id !== featureId)
        : [...prev.features, featureId],
    }));
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Typography variant="h4">Plans Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenDialog()}
          sx={{
            backgroundColor: THEME_COLORS.yellow,
            "&:hover": { backgroundColor: THEME_COLORS.darkYellow },
          }}
        >
          Add New Plan
        </Button>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell>Video Limit</TableCell>
              <TableCell>Seats</TableCell>
              <TableCell>Price per UGC</TableCell>
              <TableCell>Features</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {plans.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No plans found. Create your first plan!
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              plans.map((plan) => {
                const planFeatures = plan.features ?? [];

                return (
                  <TableRow key={plan._id}>
                    <TableCell>
                      <Typography variant="body1" fontWeight="bold">
                        {plan.name.toUpperCase()}
                      </Typography>
                    </TableCell>

                    <TableCell>{plan.videoLimit}</TableCell>
                    <TableCell>{plan.seats}</TableCell>
                    <TableCell>${plan.pricePerUGC.toFixed(2)}</TableCell>

                    <TableCell>
                      <Box display="flex" flexWrap="wrap" gap={0.5}>
                        {planFeatures.length === 0 ? (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                          >
                            No features
                          </Typography>
                        ) : (
                          planFeatures.slice(0, 3).map((feature) => (
                            <Chip
                              key={feature._id}
                              label={feature.name}
                              size="small"
                              sx={{ fontSize: "0.7rem" }}
                            />
                          ))
                        )}
                        {planFeatures.length > 3 && (
                          <Chip
                            label={`+${planFeatures.length - 3} more`}
                            size="small"
                            sx={{ fontSize: "0.7rem" }}
                          />
                        )}
                      </Box>
                    </TableCell>

                    <TableCell>
                      <Tooltip title="Edit Plan">
                        <IconButton
                          size="small"
                          onClick={() => handleOpenDialog(plan)}
                          sx={{ color: THEME_COLORS.yellow }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Plan">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(plan._id)}
                          sx={{ color: "error.main" }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingPlan ? "Edit Plan" : "Create New Plan"}
        </DialogTitle>

        <DialogContent>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Plan Name</InputLabel>
              <Select
                value={formData.name}
                label="Plan Name"
                onChange={(e) =>
                  setFormData({ ...formData, name: e.target.value })
                }
                disabled={!!editingPlan}
              >
                <MenuItem value="free">Free</MenuItem>
                <MenuItem value="starter">Starter</MenuItem>
                <MenuItem value="pro">Pro</MenuItem>
              </Select>
            </FormControl>

            <TextField
              label="Video Limit"
              type="number"
              fullWidth
              value={formData.videoLimit}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  videoLimit: parseInt(e.target.value) || 0,
                })
              }
            />

            <TextField
              label="Seats"
              type="number"
              fullWidth
              value={formData.seats}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  seats: parseInt(e.target.value) || 0,
                })
              }
            />

            <TextField
              label="Price per UGC"
              type="number"
              fullWidth
              value={formData.pricePerUGC}
              onChange={(e) =>
                setFormData({
                  ...formData,
                  pricePerUGC: parseFloat(e.target.value) || 0,
                })
              }
            />

            <Box>
              <Typography variant="subtitle1" gutterBottom>
                Features
              </Typography>

              <Paper variant="outlined" sx={{ maxHeight: 300, overflow: "auto", p: 2 }}>
                {features.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    No features available
                  </Typography>
                ) : (
                  features.map((feature) => (
                    <FormControlLabel
                      key={feature._id}
                      control={
                        <Checkbox
                          checked={formData.features.includes(feature._id)}
                          onChange={() => handleFeatureToggle(feature._id)}
                        />
                      }
                      label={
                        <Box>
                          <Typography variant="body2" fontWeight="bold">
                            {feature.name}
                          </Typography>
                          {feature.description && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              display="block"
                            >
                              {feature.description}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            display="block"
                          >
                            Credit: {feature.apiCreditEstimate}{" "}
                            {feature.apiCreditRule !== "NONE"
                              ? `(${feature.apiCreditRule})`
                              : ""}
                          </Typography>
                        </Box>
                      }
                    />
                  ))
                )}
              </Paper>
            </Box>
          </Box>
        </DialogContent>

        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSubmit}
            variant="contained"
            disabled={
              !formData.name ||
              formData.videoLimit <= 0 ||
              formData.seats <= 0 ||
              formData.pricePerUGC < 0
            }
            sx={{
              backgroundColor: THEME_COLORS.yellow,
              "&:hover": { backgroundColor: THEME_COLORS.darkYellow },
            }}
          >
            {editingPlan ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
